import { css } from '@emotion/css';
import { InputLabel, Stack, Theme, Typography } from '@mui/material';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useStyles } from '@/hooks/useStyles';

import { ChipSelect } from '../atoms/ChipSelect';

export type ChipChoice = {
  displayName: string;
  value: string;
  tooltip?: string;
  disabled?: boolean;
};

type ChipSelectorProps = {
  id?: string;
  fieldName: string;
  choices: ChipChoice[];
  label?: string;
};

export const ChipSelector = ({
  id,
  fieldName,
  choices,
  label,
}: ChipSelectorProps) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();

  return (
    <Controller
      name={fieldName}
      render={({ fieldState, field }) => {
        const error = fieldState.error;
        const errorMessage =
          typeof error?.message === 'string' ? error.message : '';

        return (
          <div>
            <InputLabel>
              <Typography variant="subtitle">{label}</Typography>
            </InputLabel>
            <div className={styles.input}>
              <Stack id={id} direction="row" spacing={20}>
                {choices.map((choice: ChipChoice) => (
                  <ChipSelect
                    id={`${id}-${choice.value}`}
                    selected={field.value === choice.value}
                    key={choice.value}
                    label={choice.displayName}
                    onClick={() => field.onChange(choice.value)}
                    tooltip={choice.tooltip}
                    disabled={choice.disabled}
                    color={errorMessage ? 'error' : undefined}
                  />
                ))}
              </Stack>
              {error ? (
                <Typography color="error" variant="caption">
                  {t(errorMessage)}
                </Typography>
              ) : null}
            </div>
          </div>
        );
      }}
    />
  );
};

const makeStyles = (theme: Theme) => ({
  input: css({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    paddingTop: theme.spacing(12),
    paddingBottom: theme.spacing(12),
  }),
});
